import { removeEmpty } from '@/utils/resultsHelper';
import { spliceItemByUUID } from '@/utils/uuidHelper';
import { approvedIcon, archivedIcon } from '@/constants/images';

const sequenceMembers = (newMembers, updatedMembers, removedMembers, currentUserId, ownerPermission) => {
  const [owners, updatedNonOwners] = updatedMembers.reduce((result, element) => {
    result[element.permissions[ownerPermission] ? 0 : 1].push(element);
    return result;
  }, [[], []]);

  const removeMe = spliceItemByUUID(removedMembers, currentUserId);
  const updateMe = spliceItemByUUID(updatedNonOwners, currentUserId);

  return {
    newMembers,
    removedMembers: {
      me: removeMe ? [removeMe] : [],
      members: removedMembers,
    },
    updatedMembers: {
      me: updateMe ? [updateMe] : [],
      members: updatedNonOwners,
      owners,
    },
  };
};

const matchMember = (user, element) => {
  if (user.uuid && user.uuid === element.uuid) {
    return user.uuid === element.uuid;
  }
  if (user.code && user.code === element.code) {
    return user.code === element.code;
  }
  return false;
};

export const membersDiff = (members, original, currentUserId, ownerPermission) => {
  const [newMembers, existingMembers] = members.reduce((result, element) => {
    result[original.some((u) => matchMember(u, element)) ? 1 : 0].push(element);
    return result;
  }, [[], []]);
  const newMembersWithPermissions = newMembers.filter((item) => Object.keys(item.permissions).length > 0);

  const [removedMembers, membersStillPresent] = original.reduce((result, element) => {
    result[existingMembers.some((u) => matchMember(u, element)) ? 1 : 0].push(element);
    return result;
  }, [[], []]);

  const updatedMembers = removeEmpty(membersStillPresent.map((existingMember) => {
    const memberInMembers = members.find((m) => matchMember(m, existingMember));
    return memberInMembers.permissions === existingMember.permissions ? null : memberInMembers;
  })).concat(newMembersWithPermissions);

  return sequenceMembers(newMembers, updatedMembers, removedMembers, currentUserId, ownerPermission);
};

export const filterUsersCreatedandUpdated = (users) => (
  users.filter((item) => (
    !((item.action === 'new' && users.some((u) => u.action === 'update' && u.uuid === item.uuid))))));

export const permissionsGroup = ({ permissions }, permissionGroups) => {
  const groups = Object.keys(permissionGroups);
  const can = Object.keys(permissions).filter((key) => permissions[key] === true);
  const candidates = groups.filter((group) => permissionGroups[group].length === can.length);

  if (can.length === 0) {
    return candidates[0];
  }

  const match = candidates.find((candidate) => (
    permissionGroups[candidate].every((key) => permissions[key] === true)));

  return match || 'custom';
};

export const userHasPermissions = (userToCheck, permissions, can = true) => (
  Object.values(permissions).every((key) => userToCheck.permissions[key] === can)
);

export const usersHavePermissions = (users, permissions, can = true) => (
  users.some((u) => userHasPermissions(u, permissions, can))
);

export const userInitials = (name) => {
  if (!name) {
    return null;
  }
  const parts = name.toUpperCase().split(' ');
  if (parts.length > 1) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
  }
  return `${parts[0].charAt(0)}`;
};

export const avatarBackgroundColour = (name) => {
  if (!name) {
    return null;
  }
  const numOfColours = 16;
  const vowels = (name.match(/[aeiou]/gi) || []).length;
  const colourIndex = (name.length + vowels) % numOfColours;

  return colourIndex;
};

const userStatusMessage = ({ approved, archived }) => {
  if (archived) { return 'status.archived'; }
  return approved ? 'status.approved' : 'status.unapproved';
};

const urlsForMe = ({ id }, url) => [
  new RegExp(`^/?users/${id}(/[^/]+)?/?$`),
  new RegExp(`^/?data/users/${id}(/[^/]+)?/?$`),
  new RegExp(`^/?tab/users/${id}(/[^/]+)?/?$`),
].some((info) => info.test(url));

export const checkCurrentUser = (currentUser, url) => {
  const props = { isGuest: currentUser ? currentUser.isGuest : true };
  props.isCurrentUser = props.isGuest ? false : urlsForMe(currentUser, url);

  return props;
};

export const userStatusContext = ({ approved, archived }, showArchive, onToggleApproved) => {
  const context = {
    className: approved ? 'approved' : 'unapproved',
    icon: archived ? archivedIcon(true) : approvedIcon(approved),
    message: approved ? 'status.unapprove' : 'status.approve',
    onClick: archived ? showArchive : onToggleApproved,
    tooltip: approved ? 'status.unapprove.hover' : 'status.approve.hover',
  };

  if (archived) {
    context.className = 'archived';
    context.message = 'status.restore';
    context.tooltip = 'status.restore.user';
  }

  return context;
};

export default userStatusMessage;
